import { ImmutableModelStore } from 'solox';
import { v4 as uuidv4 } from 'uuid';
import {
  DEFAULT_COUNT_THRESHOLD,
  MAX_LIVE_FEED_WIDGETS_PER_LAYOUT,
  MAX_WIDGETS_PER_LAYOUT,
} from './constants';
import {
  appendWidgetRowMajor,
  groupWidgetsIntoRows,
  redistributeRows,
} from './layout/gridPositions';
import { type PredefinedLayout } from './layout/predefinedLayouts';
import {
  type DayPartThreshold,
  type ServiceDisplayLayout,
  type ServiceDisplayWidget,
} from './layout/types';
import { nextWidgetPosition } from './layout/utils';

function widgetMatchesKey(w: ServiceDisplayWidget, key: string): boolean {
  return w.id === key;
}

function nextAvailableLayoutName(baseName: string, layouts: ServiceDisplayLayout[]): string {
  const taken = new Set(layouts.map((l) => l.name.toLowerCase()));
  if (!taken.has(baseName.toLowerCase())) return baseName;
  for (let i = 2; ; i++) {
    const candidate = `${baseName} (${i})`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

export const DEFAULT_LAYOUT: ServiceDisplayLayout = {
  id: '',
  name: '',
  customerId: '',
  resellerId: '',
  cloudId: '',
  widgets: [],
  createdAt: undefined,
  updatedAt: undefined,
};

export interface SDMState {
  /**
   * The layouts available to the user.
   */
  layouts: ServiceDisplayLayout[];
  /**
   * The layout we are currently selected, or undefined if we are not selecting a layout.
   */
  selectedLayout: ServiceDisplayLayout | undefined;
  /**
   * Used to find the layout after SWR refetch when creating new layouts
   */
  pendingLayoutName?: string;
  /**
   * The layout we are currently editing, or undefined if we are not editing
   * a layout.
   */
  editingLayout: ServiceDisplayLayout | undefined;
  /**
   * Origin of the editing draft. 'template' when the user picked a predefined
   * layout template, 'custom' when the user created a new layout from scratch.
   *
   */
  editingLayoutOrigin: 'template' | 'custom' | undefined;
  /**
   * Id of the predefined template the editing draft was seeded from, when
   * applicable. Used for analytics; not persisted server-side.
   */
  editingLayoutTemplateId: string | undefined;
  /**
   * Draft widget being configured before its first save. Not shown in the grid
   * until updateWidget commits it into editingLayout.
   */
  pendingNewWidget?: ServiceDisplayWidget;
  /**
   * The ID of the widget we are currently configuring, or undefined if we
   * are not configuring a widget.  If this is set, we'll show a modal dialog
   * for the given widget.
   */
  configuringWidgetId?: string;
  /**
   * Draft day-part thresholds while the widget config dialog is open.
   * When set, the dialog uses this instead of widget.thresholds; cleared on save or discard.
   */
  configuringWidgetThresholds?: DayPartThreshold[];
  /**
   * Server-assigned widget IDs that were removed during the current edit session.
   * Only populated for widgets that were previously saved (id in serverKnownWidgetIds).
   */
  pendingWidgetDeletes: string[];
  /**
   * Widget ids that exist on the server for the layout we're editing.
   * Set when we start editing; used to decide payload (omit id for new) and pendingWidgetDeletes.
   */
  serverKnownWidgetIds: string[];
  /** toggles the layout dialog used for editing layout name or creating a new layout */
  isLayoutDialogVisible: boolean;
  /** shows the predefined layout template picker dialog (SL-13146) */
  isTemplatePickerVisible: boolean;
}

export class Controller {
  public state = new ImmutableModelStore<SDMState>({
    layouts: [],
    selectedLayout: undefined,
    pendingLayoutName: undefined,
    editingLayout: undefined,
    editingLayoutOrigin: undefined,
    editingLayoutTemplateId: undefined,
    pendingNewWidget: undefined,
    configuringWidgetId: undefined,
    configuringWidgetThresholds: undefined,
    pendingWidgetDeletes: [],
    serverKnownWidgetIds: [],
    isLayoutDialogVisible: false,
    isTemplatePickerVisible: false,
  });

  private clearWidgetConfiguration(state: SDMState) {
    state.pendingNewWidget = undefined;
    state.configuringWidgetId = undefined;
    state.configuringWidgetThresholds = undefined;
  }

  setLayouts(
    layouts: ServiceDisplayLayout[],
    defaultSdmLayoutId: string | undefined,
    preferredLayoutId?: string,
  ) {
    this.state.update((state) => {
      const currentSelection = state.selectedLayout;
      state.layouts = [...layouts].sort((a, b) => a.name.localeCompare(b.name));

      // If no layouts exist, create a default one
      if (state.layouts.length === 0) {
        const defaultLayout: ServiceDisplayLayout = {
          ...DEFAULT_LAYOUT,
          name: 'Default Layout',
        };
        state.layouts = [defaultLayout];
        state.selectedLayout = defaultLayout;
        return;
      }

      // If we have a pending layout name (just created), find and select it
      if (state.pendingLayoutName) {
        const matchByName = state.layouts.find((l) => l.name === state.pendingLayoutName);
        if (matchByName) {
          state.selectedLayout = matchByName;
          state.pendingLayoutName = undefined;
          return;
        }
      }

      // Honor URL-supplied layout id if it matches a real layout
      if (preferredLayoutId) {
        const matchByPreferred = state.layouts.find((l) => l.id === preferredLayoutId);
        if (matchByPreferred) {
          state.selectedLayout = matchByPreferred;
          return;
        }
      }

      // Try to preserve current selection by ID
      if (currentSelection) {
        const matchById = state.layouts.find((l) => l.id === currentSelection.id);
        if (matchById) {
          state.selectedLayout = matchById;
          return;
        }
      }

      // Fallback to default layout if exists, otherwise oldest layout
      const defaultLayout = defaultSdmLayoutId
        ? state.layouts.find((l) => l.id === defaultSdmLayoutId)
        : undefined;

      state.selectedLayout =
        defaultLayout ??
        state.layouts.reduce((oldest, l) =>
          (l.createdAt ?? '') < (oldest.createdAt ?? '') ? l : oldest,
        );
    });
  }

  /** Sets selected layout by ID */
  selectLayout(layoutId: string | undefined) {
    this.state.update((state) => {
      const layout = layoutId
        ? state.layouts.find((l: ServiceDisplayLayout) => l.id === layoutId)
        : undefined;
      state.selectedLayout = layout;
    });
  }

  /** Set the pending layout name to find after SWR refetch */
  setSelectedLayoutName(name: string) {
    this.state.update((state) => {
      state.pendingLayoutName = name;
    });
  }

  /** opens dialog used to create/edit layout. Gets layout to rename or create a new layout */
  // undefined is used for creating a new layout template
  displayLayoutDialog(
    isVisible: boolean,
    editLayoutId: string | undefined,
    newLayoutBaseName = 'Layout',
  ) {
    this.state.update((state) => {
      state.editingLayout = editLayoutId
        ? state.layouts.find((l: ServiceDisplayLayout) => l.id === editLayoutId)
        : {
            ...DEFAULT_LAYOUT,
            id: uuidv4(),
            name: nextAvailableLayoutName(newLayoutBaseName, state.layouts),
          };
      state.serverKnownWidgetIds = state.editingLayout?.widgets?.map((w) => w.id ?? '') ?? [];
      state.isLayoutDialogVisible = isVisible;
      state.isTemplatePickerVisible = false;
      state.editingLayoutOrigin = editLayoutId ? 'custom' : undefined;
      state.editingLayoutTemplateId = undefined;
    });
  }

  showTemplatePicker() {
    this.state.update((state) => {
      state.isTemplatePickerVisible = true;
    });
  }

  hideTemplatePicker() {
    this.state.update((state) => {
      state.isTemplatePickerVisible = false;
      const draft = state.editingLayout;
      const isPersistedLayout = draft && state.layouts.some((l) => l.id === draft.id);
      if (draft && !isPersistedLayout) {
        state.editingLayout = undefined;
        this.clearWidgetConfiguration(state);
        state.pendingWidgetDeletes = [];
        state.serverKnownWidgetIds = [];
        state.editingLayoutOrigin = undefined;
        state.editingLayoutTemplateId = undefined;
      }
    });
  }

  backToTemplatePicker() {
    this.state.update((state) => {
      state.isLayoutDialogVisible = false;
      state.isTemplatePickerVisible = true;
    });
  }

  startLayoutFromTemplate(
    template: PredefinedLayout,
    resolveTagId: (name: string) => string = (name) => name,
  ) {
    this.state.update((state) => {
      state.editingLayout = {
        ...DEFAULT_LAYOUT,
        id: uuidv4(),
        name: nextAvailableLayoutName(template.name, state.layouts),
        widgets: template.widgets.map((w) => ({
          id: uuidv4(),
          title: w.title,
          tagIds: w.tagNames.map(resolveTagId),
          config: { ...w.config },
          position: { ...w.position },
          thresholds: [],
        })),
      };
      state.serverKnownWidgetIds = [];
      state.pendingWidgetDeletes = [];
      this.clearWidgetConfiguration(state);
      state.isLayoutDialogVisible = true;
      state.isTemplatePickerVisible = false;
      state.editingLayoutOrigin = 'template';
      state.editingLayoutTemplateId = template.id;
    });
  }

  /** Duplicate a layout - copies all data except id, with a unique default name */
  duplicateLayout(layoutId: string, newLayoutBaseName = 'Layout') {
    this.state.update((state) => {
      const layoutToDuplicate = state.layouts.find((l) => l.id === layoutId);
      if (!layoutToDuplicate) {
        throw new Error(`Layout not found: ${layoutId}`);
      }

      state.editingLayout = {
        ...layoutToDuplicate,
        id: uuidv4(),
        name: nextAvailableLayoutName(newLayoutBaseName, state.layouts),
        widgets: layoutToDuplicate.widgets.map((widget) => ({
          ...widget,
          id: uuidv4(),
          position: { ...widget.position },
          config: { ...widget.config },
          tagIds: [...widget.tagIds],
        })),
        createdAt: undefined,
        updatedAt: undefined,
      };
      state.serverKnownWidgetIds = []; // all widgets are new copies
      state.isLayoutDialogVisible = true;
      state.editingLayoutOrigin = 'custom';
      state.editingLayoutTemplateId = undefined;
    });
  }

  layoutEditStart(layoutOrId: string) {
    this.state.update((state) => {
      if (layoutOrId == null) return;
      state.editingLayout = state.layouts.find((l) => l.id === layoutOrId);
      state.serverKnownWidgetIds = state.editingLayout?.widgets?.map((w) => w.id ?? '') ?? [];
      state.editingLayoutOrigin = 'custom';
      state.editingLayoutTemplateId = undefined;
    });
  }

  /** Stop editing a layout.  This discards any changes to the layout. */
  discardLayoutEdits() {
    this.state.update({
      editingLayout: undefined,
      pendingNewWidget: undefined,
      configuringWidgetId: undefined,
      configuringWidgetThresholds: undefined,
      pendingWidgetDeletes: [],
      serverKnownWidgetIds: [],
      editingLayoutOrigin: undefined,
      editingLayoutTemplateId: undefined,
    });
  }

  /** Opens the widget config dialog for a new widget without changing the grid. */
  updateLayoutWithNewConfig() {
    const snapshot = this.state.getSnapshot();
    if (!snapshot.editingLayout) {
      throw new Error('Not editing a layout');
    }

    // Returns undefined once the layout is full, which the guard below handles.
    const position = nextWidgetPosition(snapshot.editingLayout.widgets);
    if (!position) {
      return;
    }

    this.state.update((state) => {
      const newWidget: ServiceDisplayWidget = {
        id: uuidv4(),
        tagIds: [],
        title: 'New Widget',
        config: {
          type: 'COUNT',
          threshold: DEFAULT_COUNT_THRESHOLD,
          horizon: 'realtime',
        },
        position,
        thresholds: [],
      };
      state.pendingNewWidget = newWidget;
      state.configuringWidgetId = newWidget.id;
      state.configuringWidgetThresholds = undefined;
    });
  }

  private commitPendingNewWidget(state: SDMState, widget: ServiceDisplayWidget) {
    if (!state.editingLayout || !state.pendingNewWidget) {
      throw new Error('No pending new widget to commit');
    }

    if (state.editingLayout.widgets.length >= MAX_WIDGETS_PER_LAYOUT) {
      throw new Error('Layout is full');
    }

    // Keep existing widgets in their rows; grow at the bottom left-to-right
    // (append to bottom row, else a new row, else bottom-most incomplete row).
    const rows = groupWidgetsIntoRows(state.editingLayout.widgets);
    state.editingLayout.widgets = redistributeRows(appendWidgetRowMajor(rows, widget)).flat();
    this.clearWidgetConfiguration(state);
  }

  /**
   * Live feed widgets hold WebRTC connections open, so a layout may only contain a couple of
   * them. The dialog hides the option once the cap is reached; this is the backstop.
   */
  private assertLiveFeedCapacity(
    widgets: readonly ServiceDisplayWidget[],
    widget: ServiceDisplayWidget,
  ) {
    if (widget.config.type !== 'LIVE_FEED') {
      return;
    }

    const key = widget.id ?? '';
    const others = widgets.filter(
      (w) => w.config.type === 'LIVE_FEED' && !widgetMatchesKey(w, key),
    );
    if (others.length >= MAX_LIVE_FEED_WIDGETS_PER_LAYOUT) {
      throw new Error('Layout already has the maximum number of live feed widgets');
    }
  }

  /** Show the configuration modal for the given widget. */
  widgetEditStart(id: string) {
    this.state.update({ configuringWidgetId: id, configuringWidgetThresholds: undefined });
  }

  setConfiguringWidgetThresholds(thresholds: DayPartThreshold[]) {
    this.state.update({ configuringWidgetThresholds: thresholds });
  }

  /**
   * Discard edits to the current widget. A widget already in `editingLayout` stays as it
   * was, even if the server does not know it yet; only an uncommitted pendingNewWidget is dropped.
   */
  discardWidgetEdits = () => {
    this.state.update((state) => {
      this.clearWidgetConfiguration(state);
    });
  };

  /**
   * Update an existing widget in the layout, and stop editing the widget.
   *
   * Note that this just updates the layout.  The user still has to save the
   * layout itself to persist the changes.
   *
   * Returns the ID of the updated widget.
   */
  updateWidget(widget: ServiceDisplayWidget): string {
    const key = widget.id ?? '';

    this.state.update((state) => {
      if (!state.editingLayout) {
        throw new Error('Not editing a layout');
      }

      this.assertLiveFeedCapacity(state.editingLayout.widgets, widget);

      if (state.pendingNewWidget && widgetMatchesKey(state.pendingNewWidget, key)) {
        this.commitPendingNewWidget(state, widget);
        return;
      }

      const index = state.editingLayout.widgets.findIndex((w) => widgetMatchesKey(w, key));
      if (index === -1) {
        throw new Error(`Widget not found: ${key}`);
      }

      // Keep the layout position — form saves (including the follow-up tag-id
      // remap) must not overwrite placement with a stale draft position.
      state.editingLayout.widgets[index] = {
        ...widget,
        position: state.editingLayout.widgets[index].position,
      };
      this.clearWidgetConfiguration(state);
    });
    return key;
  }

  /**
   * Apply a drag-and-drop result. `rows` is widget ids top-to-bottom and
   * left-to-right after the move; positions are recomputed via redistributeRows.
   */
  commitWidgetRows(rows: string[][]) {
    this.state.update((state) => {
      if (!state.editingLayout) {
        throw new Error('Not editing a layout');
      }

      const widgetsById = new Map(state.editingLayout.widgets.map((w) => [w.id, w]));
      const rowsOfWidgets = rows.map((row) =>
        row.map((id) => widgetsById.get(id)).filter((w): w is ServiceDisplayWidget => !!w),
      );

      state.editingLayout.widgets = redistributeRows(rowsOfWidgets).flat();
    });
  }

  /** Remove a widget from the layout. */
  removeWidget(id: string) {
    this.state.update((state) => {
      if (!state.editingLayout) {
        throw new Error('Not editing a layout');
      }

      const widgetToRemove = state.editingLayout.widgets.find((w) => w.id === id);
      if (widgetToRemove?.id && state.serverKnownWidgetIds.includes(widgetToRemove.id)) {
        state.pendingWidgetDeletes.push(widgetToRemove.id);
      }

      // Keep every other widget in its row; only the removed widget's row spreads
      // to refill the gap. An emptied row is dropped (rows restack).
      const rows = groupWidgetsIntoRows(state.editingLayout.widgets)
        .map((row) => row.filter((w) => w.id !== id))
        .filter((row) => row.length > 0);
      state.editingLayout.widgets = redistributeRows(rows).flat();
    });
  }

  /** Clear pending deletes after a successful save. */
  clearPendingWidgetDeletes() {
    this.state.update({ pendingWidgetDeletes: [] });
  }
}
